/**
 * useDarkMode Hook
 * Feature: 010-modern-ui-2026
 *
 * Manages dark mode state with system preference detection,
 * manual toggle, and localStorage persistence
 */

import { useState, useEffect, useCallback } from 'react';

const DARK_MODE_STORAGE_KEY = 'talksy-dark-mode';
const DARK_CLASS = 'dark';

/**
 * Get system preference for dark mode
 */
function getSystemPreference(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/**
 * Get stored preference from localStorage
 */
function getStoredPreference(): boolean | null {
  if (typeof window === 'undefined') return null;
  const stored = localStorage.getItem(DARK_MODE_STORAGE_KEY);
  if (stored === null) return null;
  return stored === 'true';
}

/**
 * Apply dark mode class to document
 */
function applyDarkMode(isDark: boolean): void {
  if (typeof document === 'undefined') return;
  
  const root = document.documentElement;
  if (isDark) {
    root.classList.add(DARK_CLASS);
  } else {
    root.classList.remove(DARK_CLASS);
  }
}

/**
 * Hook to manage dark mode state
 *
 * @returns Object with isDark state and toggle function
 *
 * @example
 * ```tsx
 * const { isDark, toggle } = useDarkMode();
 *
 * return (
 *   <button onClick={toggle}>
 *     {isDark ? 'Light Mode' : 'Dark Mode'}
 *   </button>
 * );
 * ```
 */
export function useDarkMode() {
  // Initialize state: stored preference > system preference > false
  const [isDark, setIsDark] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    
    const stored = getStoredPreference();
    if (stored !== null) {
      return stored;
    }
    
    return getSystemPreference();
  });

  // Apply dark mode class on mount and when state changes
  useEffect(() => {
    applyDarkMode(isDark);
  }, [isDark]);

  // Listen to system preference changes (only if no stored preference)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    // If user has manually set preference, don't listen to system changes
    const stored = getStoredPreference();
    if (stored !== null) return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e: MediaQueryListEvent) => {
      setIsDark(e.matches);
    };

    // Set initial value
    setIsDark(mediaQuery.matches);

    // Listen for changes
    mediaQuery.addEventListener('change', handleChange);
    
    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  /**
   * Toggle dark mode manually
   */
  const toggle = useCallback(() => {
    setIsDark((prev) => {
      const newValue = !prev;
      // Store preference in localStorage
      localStorage.setItem(DARK_MODE_STORAGE_KEY, String(newValue));
      return newValue;
    });
  }, []);

  /**
   * Set dark mode explicitly
   */
  const setDarkMode = useCallback((value: boolean) => {
    setIsDark(value);
    localStorage.setItem(DARK_MODE_STORAGE_KEY, String(value));
  }, []);

  return {
    isDark,
    toggle,
    setDarkMode,
  };
}
